import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import { useUser } from '../../auth/hooks';
import { type GraphData, type GraphNode, type GraphEdge, type GraphSchema, type UndoableTransaction, type UndoResponse, type UndoConflict } from '../../api/dastarkhan';
import type { ColorByAttribute } from './GraphCanvas';
import { BulkUploadPanel } from '../upload/BulkUploadPanel';
import { EdgeTypeCombobox } from './EdgeTypeCombobox';
import { PropertyEditor } from './PropertyEditor';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Button,
  Input,
  Card,
  Select,
  Stack,
  Flex,
  Modal,
  Icon,
  useTheme,
  spacing,
} from '@fti/design-system';
import { coerceSourceStatus } from '../../utils/sourceStatus';

// Simple autocomplete for node selection by name/label
function NodeAutocomplete({ nodes, value, onChange, label, placeholder }: {
  nodes: GraphNode[];
  value: string;
  onChange: (elementId: string) => void;
  label?: string;
  placeholder?: string;
}) {
  const { theme } = useTheme();
  const [text, setText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const nodeOptions = useMemo(() => nodes.map((n) => {
    const name = (n.properties.name as string) || (n.properties.Name as string) || (n.properties.title as string) || '';
    const nodeLabel = n.labels[0] || '';
    const display = name ? `${name} (${nodeLabel})` : `${nodeLabel}:${n.element_id.slice(-8)}`;
    return { node: n, display, searchText: `${name} ${nodeLabel} ${n.element_id}`.toLowerCase() };
  }), [nodes]);

  useEffect(() => {
    if (value) {
      const match = nodeOptions.find((o) => o.node.element_id === value);
      if (match) setText(match.display);
    } else {
      setText('');
    }
  }, [value, nodeOptions]);

  const filtered = text.trim()
    ? nodeOptions.filter((o) => o.searchText.includes(text.toLowerCase())).slice(0, 8)
    : nodeOptions.slice(0, 8);

  const selectOption = (opt: typeof filtered[0]) => {
    setText(opt.display);
    onChange(opt.node.element_id);
    setShowSuggestions(false);
    setHighlightIndex(0);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filtered.length === 0) return;

    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      selectOption(filtered[highlightIndex]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', minWidth: '200px', zIndex: showSuggestions ? 9999 : 'auto' }}>
      <Input
        label={label}
        size="sm"
        value={text}
        autoComplete="one-time-code"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setText(e.target.value);
          onChange('');
          setShowSuggestions(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Type node name...'}
      />
      {showSuggestions && filtered.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: Math.max(wrapperRef.current?.offsetWidth || 200, 250),
          zIndex: 99999,
          background: theme.colors.background.elevated,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '4px',
          maxHeight: '200px',
          overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          marginTop: '2px',
          fontFamily: "'Source Sans 3', sans-serif",
        }}>
          {filtered.map((opt, i) => (
            <div
              key={opt.node.element_id}
              style={{
                padding: `${spacing.xs} ${spacing.sm}`,
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontFamily: "'Source Sans 3', sans-serif",
                color: theme.colors.text.primary,
                background: i === highlightIndex ? theme.colors.background.paper : 'transparent',
                borderBottom: `1px solid ${theme.colors.border.default}`,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                selectOption(opt);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
            >
              {opt.display}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface GraphToolbarProps {
  projectId: string;
  nodes: GraphNode[];
  schema: GraphSchema | null;
  onNodeCreated: () => void;
  onEdgeCreated: () => void;
  onExport: (format: string, nodeIds?: string[]) => void;
  colorBy: ColorByAttribute;
  onColorByChange: (attr: ColorByAttribute) => void;
  edgeFilterKey: string | null;
  edgeFilterValue: string;
  onEdgeFilterKeyChange: (key: string | null) => void;
  onEdgeFilterValueChange: (value: string) => void;
  edgePropertyKeys: string[];
  lastUndoable: UndoableTransaction | null;
  undoLoading: boolean;
  onUndo: (transactionId: number, force?: boolean) => Promise<UndoResponse | UndoConflict | undefined>;
  onSearchNodes: (query: string) => void;
  selectedNodeCount: number;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onHighlight: (elementIds: string[]) => void;
  onClearHighlights: () => void;
  edges: GraphEdge[];
  showAIExplorer: boolean;
  onToggleAIExplorer: () => void;
  showCypherConsole: boolean;
  onToggleCypherConsole: () => void;
  showLayoutPanel: boolean;
  onToggleLayoutPanel: () => void;
  layoutRunning: boolean;
  onToggleLayoutRun: () => void;
  hasCypherSubgraph: boolean;
  onClearCypherSubgraph: () => void;
}

type FormPanel = 'none' | 'add-node' | 'add-edge' | 'upload';

export const GraphToolbar: React.FC<GraphToolbarProps> = ({
  projectId, nodes, schema, onNodeCreated, onEdgeCreated, onExport, colorBy, onColorByChange, edgeFilterKey, edgeFilterValue, onEdgeFilterKeyChange, onEdgeFilterValueChange, edgePropertyKeys, lastUndoable, undoLoading, onUndo, onSearchNodes, selectedNodeCount, isFullscreen, onToggleFullscreen, onHighlight, onClearHighlights, edges, showAIExplorer, onToggleAIExplorer, showCypherConsole, onToggleCypherConsole, showLayoutPanel, onToggleLayoutPanel, layoutRunning, onToggleLayoutRun, hasCypherSubgraph, onClearCypherSubgraph,
}) => {
  const { theme } = useTheme();
  const { canCreate, canUpdate } = usePermissions();
  const { fetchData } = useAuthenticatedFetch();
  const currentUser = useUser();

  // Inline form panel (replaces modals)
  const [activeForm, setActiveForm] = useState<FormPanel>('none');

  // Create node — structured fields
  const [nodeLabel, setNodeLabel] = useState('');
  const [nodeName, setNodeName] = useState('');
  const [nodeDescription, setNodeDescription] = useState('');
  const [nodeJurisdiction, setNodeJurisdiction] = useState('');
  const [nodeAnalyst, setNodeAnalyst] = useState('');
  const [nodeRiskTags, setNodeRiskTags] = useState('');
  const [nodeAliases, setNodeAliases] = useState('');
  const [nodeSourceRef, setNodeSourceRef] = useState('');
  const [nodeSourceRefs, setNodeSourceRefs] = useState('');
  const [nodeAdHocProps, setNodeAdHocProps] = useState<Record<string, unknown>>({});
  const [nodeCreating, setNodeCreating] = useState(false);

  // Create edge
  const [edgeType, setEdgeType] = useState('');
  const [edgeSource, setEdgeSource] = useState('');
  const [edgeTarget, setEdgeTarget] = useState('');
  const [edgeSourceStatus, setEdgeSourceStatus] = useState('');
  const [edgeSourceRef, setEdgeSourceRef] = useState('');
  const [edgeProps, setEdgeProps] = useState<Record<string, unknown>>({});
  const [edgeCreating, setEdgeCreating] = useState(false);


  // Jurisdiction suggestions from existing graph data
  const jurisdictionSuggestions = useMemo(() => {
    const vals = new Set<string>();
    nodes.forEach((n) => {
      const j = n.properties.jurisdiction as string | undefined;
      if (j) vals.add(j);
    });
    return Array.from(vals).sort();
  }, [nodes]);

  // Default analyst to current user email on form open
  useEffect(() => {
    if (activeForm === 'add-node' && !nodeAnalyst && currentUser?.email) {
      setNodeAnalyst(currentUser.email);
    }
  }, [activeForm, nodeAnalyst, currentUser]);

  // Export
  const [exportFormat, setExportFormat] = useState('html');

  // Undo
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [showConflict, setShowConflict] = useState(false);
  const [conflictData, setConflictData] = useState<UndoConflict | null>(null);
  const [undoMessage, setUndoMessage] = useState<string | null>(null);

  const parseCommaSeparated = (s: string): string[] =>
    s.split(',').map((v) => v.trim()).filter(Boolean);

  const resetNodeForm = () => {
    setNodeLabel(''); setNodeName(''); setNodeDescription('');
    setNodeJurisdiction(''); setNodeAnalyst(''); setNodeRiskTags('');
    setNodeAliases(''); setNodeSourceRef(''); setNodeSourceRefs('');
    setNodeAdHocProps({});
  };

  const handleCreateNode = async () => {
    if (!nodeLabel.trim()) return;
    setNodeCreating(true);
    try {
      const props: Record<string, unknown> = {};
      // Structured fields — only include non-empty
      if (nodeName.trim()) props.name = nodeName.trim();
      if (nodeDescription.trim()) props.description = nodeDescription.trim();
      if (nodeJurisdiction.trim()) props.jurisdiction = nodeJurisdiction.trim();
      if (nodeAnalyst.trim()) props.analyst = nodeAnalyst.trim();
      if (nodeRiskTags.trim()) {
        const tags = parseCommaSeparated(nodeRiskTags);
        if (tags.length > 0) props.risk_tags = tags;
      }
      if (nodeAliases.trim()) {
        const aliases = parseCommaSeparated(nodeAliases);
        if (aliases.length > 0) props.aliases = aliases;
      }
      if (nodeSourceRef.trim()) props.source_reference = nodeSourceRef.trim();
      if (nodeSourceRefs.trim()) {
        const refs = parseCommaSeparated(nodeSourceRefs);
        if (refs.length > 0) props.source_references = refs;
      }
      // Merge ad-hoc properties
      for (const [k, v] of Object.entries(nodeAdHocProps)) {
        if (k.trim() && v != null) props[k.trim()] = v;
      }
      await fetchData(`/dastarkhan/projects/${projectId}/graph/nodes`, {
        method: 'POST',
        body: JSON.stringify({ labels: [nodeLabel.trim()], properties: props }),
      });
      setActiveForm('none');
      resetNodeForm();
      onNodeCreated();
    } catch { /* handled */ }
    finally { setNodeCreating(false); }
  };

  const handleCreateEdge = async () => {
    if (!edgeType.trim() || !edgeSource.trim() || !edgeTarget.trim()) return;
    setEdgeCreating(true);
    try {
      const props: Record<string, unknown> = { ...edgeProps };
      const coercedStatus = coerceSourceStatus(edgeSourceStatus);
      if (coercedStatus) props.source_status = coercedStatus;
      if (edgeSourceRef.trim()) props.source_reference = edgeSourceRef.trim();

      await fetchData(`/dastarkhan/projects/${projectId}/graph/edges`, {
        method: 'POST',
        body: JSON.stringify({
          type: edgeType.trim(),
          source_node_id: edgeSource.trim(),
          target_node_id: edgeTarget.trim(),
          properties: Object.keys(props).length > 0 ? props : undefined,
        }),
      });
      setActiveForm('none');
      setEdgeType(''); setEdgeSource(''); setEdgeTarget('');
      setEdgeSourceStatus(''); setEdgeSourceRef(''); setEdgeProps({});
      onEdgeCreated();
    } catch { /* handled */ }
    finally { setEdgeCreating(false); }
  };


  const handleUndoConfirm = async () => {
    if (!lastUndoable) return;
    try {
      const result = await onUndo(lastUndoable.id);
      if (!result) return;
      if (result.status === 'success') {
        setShowUndoConfirm(false);
        setUndoMessage('Undo successful');
        setTimeout(() => setUndoMessage(null), 3000);
      } else if (result.status === 'conflict') {
        setShowUndoConfirm(false);
        setConflictData(result as UndoConflict);
        setShowConflict(true);
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number };
      if (apiErr?.status === 422) {
        setShowUndoConfirm(false);
        setUndoMessage('This transaction was already reversed');
        setTimeout(() => setUndoMessage(null), 3000);
      }
    }
  };

  const handleForceUndo = async () => {
    if (!conflictData) return;
    try {
      const result = await onUndo(conflictData.transaction_id, true);
      if (result && result.status === 'success') {
        setShowConflict(false);
        setConflictData(null);
        setUndoMessage('Undo successful (forced)');
        setTimeout(() => setUndoMessage(null), 3000);
      }
    } catch { /* handled */ }
  };

  function undoSummary(tx: UndoableTransaction): string {
    const opVerbs: Record<string, string> = {
      create: 'delete the created',
      update: 'restore the previous properties of the',
      delete: 'recreate the deleted',
    };
    return `This will ${opVerbs[tx.operation] || 'undo the'} ${tx.entity_type}.`;
  }

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  const handleExport = () => {
    onExport(exportFormat);
  };

  const exportFormatOptions = [
    { value: 'html', label: 'Interactive HTML' },
    { value: 'svg', label: 'SVG Image' },
    { value: 'png', label: 'PNG Image' },
    { value: 'gexf', label: 'Gephi (GEXF)' },
    { value: 'pkl', label: 'NetworkX (PKL)' },
  ];

  return (
    <div>
      {/* Match toolbar Input height to Select/Button sm (~32px) so inputs
          don't stick out above their neighbors. Scoped to the toolbar
          via .toolbar-compact-input so other DS Input consumers
          (node/edge create forms) keep their default size. */}
      <style>{`
        .toolbar-compact-input input { min-height: 32px !important; height: 32px; padding-top: 4px; padding-bottom: 4px; }
      `}</style>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: spacing.sm,
        padding: `${spacing.xs} ${spacing.sm}`,
        borderBottom: `1px solid ${theme.colors.border.default}`,
        background: theme.colors.background.paper,
        alignItems: 'center',
        minHeight: '40px',
        overflow: 'visible',
      }}>
        {/* Left: CRUD buttons */}
        {canCreate(projectId) && (
          <Flex gap="xs" align="center">
            <Button variant={activeForm === 'add-node' ? 'solid' : 'outline'} color="primary" size="sm" onClick={() => setActiveForm(activeForm === 'add-node' ? 'none' : 'add-node')}>Add Node</Button>
            <Button variant={activeForm === 'add-edge' ? 'solid' : 'outline'} size="sm" onClick={() => setActiveForm(activeForm === 'add-edge' ? 'none' : 'add-edge')}>Add Edge</Button>
            <Button variant={activeForm === 'upload' ? 'solid' : 'outline'} size="sm" onClick={() => setActiveForm(activeForm === 'upload' ? 'none' : 'upload')}>Upload</Button>
          </Flex>
        )}

        {/* Layout / Cypher / AI Explorer toggles — visible to all users */}
        <Flex gap="xs" align="center">
          <Button variant={showLayoutPanel ? 'solid' : 'outline'} size="sm" onClick={onToggleLayoutPanel}>Layout</Button>
          <Button variant={showCypherConsole ? 'solid' : 'outline'} size="sm" onClick={onToggleCypherConsole}>Cypher</Button>
          <Button variant={showAIExplorer ? 'solid' : 'outline'} size="sm" onClick={onToggleAIExplorer}>AI Explorer</Button>
          <Button variant={layoutRunning ? 'solid' : 'outline'} size="sm" onClick={onToggleLayoutRun}>
            {layoutRunning ? '■ Stop' : '▶ Play'}
          </Button>
          {hasCypherSubgraph && (
            <Button variant="outline" size="sm" onClick={onClearCypherSubgraph} title="Clear the rendered Cypher subgraph and return to the main graph">
              ✕ Clear Cypher
            </Button>
          )}
        </Flex>

        {/* Node Color-by */}
        <div style={{ width: '160px', flexShrink: 0 }}>
          <Select size="sm" value={colorBy} onChange={(value: string) => onColorByChange(value as ColorByAttribute)}
            options={[{ value: '_entityType', label: 'Color: Entity Type' }, { value: '_jurisdiction', label: 'Color: Jurisdiction' }]} />
        </div>

        {/* Edge property filter */}
        {edgePropertyKeys.length > 0 && (
          <Flex gap="xs" align="center">
            <div style={{ width: '130px', flexShrink: 0 }}>
              <Select
                size="sm"
                value={edgeFilterKey || ''}
                onChange={(value: string) => {
                  onEdgeFilterKeyChange(value || null);
                  if (!value) onEdgeFilterValueChange('');
                }}
                options={[
                  { value: '', label: 'Edge filter...' },
                  ...edgePropertyKeys.map((k) => ({ value: k, label: `Edge: ${k}` })),
                ]}
              />
            </div>
            {edgeFilterKey && (
              <>
                <div className="toolbar-compact-input" style={{ width: '120px', flexShrink: 0, alignSelf: 'center' }}>
                  <Input
                    size="sm"
                    value={edgeFilterValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEdgeFilterValueChange(e.target.value)}
                    placeholder="Value..."
                  />
                </div>
                {edgeFilterValue && (
                  <Button variant="ghost" size="sm" onClick={() => { onEdgeFilterKeyChange(null); onEdgeFilterValueChange(''); }}>Clear</Button>
                )}
              </>
            )}
          </Flex>
        )}

        {/* Node search */}
        <div className="toolbar-compact-input" style={{ width: '130px', flexShrink: 0, alignSelf: 'center' }}>
          <Input
            size="sm"
            placeholder="Search nodes"
            autoComplete="one-time-code"
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') onSearchNodes((e.target as HTMLInputElement).value);
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.value === '') onSearchNodes('');
            }}
          />
        </div>

        {/* Spacer pushes export to right */}
        <div style={{ flex: 1 }} />

        {/* Undo message */}
        {undoMessage && (
          <span style={{ fontSize: '0.8rem', color: theme.colors.semantic.success.default }}>{undoMessage}</span>
        )}

        {/* Undo button */}
        {canUpdate(projectId) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUndoConfirm(true)}
            disabled={!lastUndoable || undoLoading}
            title={lastUndoable ? `Undo ${lastUndoable.operation} ${lastUndoable.entity_type} — ${timeAgo(lastUndoable.created_at)}` : 'Nothing to undo'}
          >
            <Flex gap="xs" align="center">
              <Icon name="arrow-left" size={14} />
              <span>Undo</span>
            </Flex>
          </Button>
        )}

        {/* Fullscreen toggle */}
        {isFullscreen ? (
          <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>Esc to exit</span>
        ) : (
          <Button variant="ghost" size="sm" onClick={onToggleFullscreen} title="Fullscreen">
            Fullscreen
          </Button>
        )}

        {/* Right: Export group */}
        <Flex gap="xs" align="center">
          <div style={{ width: '160px', flexShrink: 0 }}>
            <Select size="sm" value={exportFormat} onChange={(value: string) => setExportFormat(value)} options={exportFormatOptions} />
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={nodes.length === 0}>
            {selectedNodeCount >= 2 ? 'Export highlighted nodes' : 'Export'}
          </Button>
        </Flex>
      </div>

      {/* Inline form panels — no modals, no focus issues */}
      {activeForm === 'add-node' && (
        <Card style={{ borderRadius: 0, borderBottom: `1px solid ${theme.colors.border.default}`, overflow: 'visible' }}>
          <Card.Body>
            <Stack gap="sm">
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input label="Name" size="sm" value={nodeName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeName(e.target.value)} placeholder="Entity name" />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <Input label="Label (required)" size="sm" value={nodeLabel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeLabel(e.target.value)} placeholder="e.g., Person" list="label-suggestions" />
                  <datalist id="label-suggestions">
                    {(schema?.node_labels || []).map((l) => <option key={l} value={l} />)}
                  </datalist>
                </div>
              </Flex>
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '250px', flex: 1 }}>
                  <Input label="Description" size="sm" value={nodeDescription} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeDescription(e.target.value)} placeholder="Brief description" />
                </div>
              </Flex>
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '150px', position: 'relative' }}>
                  <Input label="Jurisdiction" size="sm" value={nodeJurisdiction} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeJurisdiction(e.target.value)} placeholder="e.g., BVI" list="jurisdiction-suggestions" />
                  <datalist id="jurisdiction-suggestions">
                    {jurisdictionSuggestions.map((j) => <option key={j} value={j} />)}
                  </datalist>
                </div>
                <div style={{ minWidth: '200px' }}>
                  <Input label="Analyst" size="sm" value={nodeAnalyst} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeAnalyst(e.target.value)} placeholder="analyst@fticonsulting.com" />
                </div>
              </Flex>
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input label="Risk Tags" size="sm" value={nodeRiskTags} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeRiskTags(e.target.value)} placeholder="Comma-separated, e.g. PEP, sanctions" />
                </div>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input label="Aliases" size="sm" value={nodeAliases} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeAliases(e.target.value)} placeholder="Comma-separated alternate names" />
                </div>
              </Flex>
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input label="Source Reference" size="sm" value={nodeSourceRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeSourceRef(e.target.value)} placeholder="Primary source citation" />
                </div>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input label="Additional Sources" size="sm" value={nodeSourceRefs} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNodeSourceRefs(e.target.value)} placeholder="Comma-separated additional sources" />
                </div>
              </Flex>
              <div>
                <span style={{ fontSize: '0.7rem', color: theme.colors.text.secondary }}>Additional Properties (optional)</span>
                <div style={{ marginTop: '2px' }}>
                  <PropertyEditor properties={nodeAdHocProps} onChange={setNodeAdHocProps} />
                </div>
              </div>
              <Flex gap="xs">
                <Button variant="solid" color="primary" size="sm" onClick={handleCreateNode} disabled={nodeCreating || !nodeLabel.trim()}>
                  {nodeCreating ? 'Creating...' : 'Create Node'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setActiveForm('none'); resetNodeForm(); }}>Cancel</Button>
              </Flex>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {activeForm === 'add-edge' && (
        <Card style={{ borderRadius: 0, borderBottom: `1px solid ${theme.colors.border.default}`, overflow: 'visible' }}>
          <Card.Body>
            <Stack gap="sm">
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <EdgeTypeCombobox
                  value={edgeType}
                  onChange={setEdgeType}
                  existingTypes={schema?.relationship_types || []}
                />
                <NodeAutocomplete
                  nodes={nodes}
                  value={edgeSource}
                  onChange={setEdgeSource}
                  label="Source Node"
                  placeholder="Type node name..."
                />
                <NodeAutocomplete
                  nodes={nodes}
                  value={edgeTarget}
                  onChange={setEdgeTarget}
                  label="Target Node"
                  placeholder="Type node name..."
                />
              </Flex>
              <Flex gap="md" align="end" style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: '150px' }}>
                  <Select
                    label="Source Status"
                    size="sm"
                    value={edgeSourceStatus}
                    onChange={(value: string) => setEdgeSourceStatus(value)}
                    options={[
                      { value: '', label: 'Select status...' },
                      { value: 'VERIFIED', label: 'Verified' },
                      { value: 'UNVERIFIED', label: 'Unverified' },
                    ]}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: 1 }}>
                  <Input
                    label="Source Reference"
                    size="sm"
                    value={edgeSourceRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdgeSourceRef(e.target.value)}
                    placeholder="Source citation"
                  />
                </div>
              </Flex>
              <div>
                <span style={{ fontSize: '0.7rem', color: theme.colors.text.secondary }}>Additional Properties (optional)</span>
                <div style={{ marginTop: '2px' }}>
                  <PropertyEditor
                    properties={edgeProps}
                    onChange={setEdgeProps}
                  />
                </div>
              </div>
              <Flex gap="xs">
                <Button variant="solid" color="primary" size="sm" onClick={handleCreateEdge} disabled={edgeCreating || !edgeType.trim()}>
                  {edgeCreating ? 'Creating...' : 'Create Edge'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setActiveForm('none'); setEdgeSourceStatus(''); setEdgeSourceRef(''); setEdgeProps({}); }}>Cancel</Button>
              </Flex>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {activeForm === 'upload' && (
        <BulkUploadPanel
          projectId={projectId}
          onUploadComplete={onNodeCreated}
          onClose={() => setActiveForm('none')}
        />
      )}

      {/* Undo confirmation modal */}
      {showUndoConfirm && lastUndoable && (
        <Modal isOpen onClose={() => setShowUndoConfirm(false)}>
          <Modal.Header>Undo Last Change</Modal.Header>
          <Modal.Body>
            <Stack gap="sm">
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Operation:</strong> {lastUndoable.operation} {lastUndoable.entity_type}
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Entity ID:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }}>{lastUndoable.entity_neo4j_id}</span>
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Time:</strong> {new Date(lastUndoable.created_at).toLocaleString()}
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: theme.colors.text.secondary }}>
                {undoSummary(lastUndoable)}
              </p>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" size="sm" onClick={() => setShowUndoConfirm(false)}>Cancel</Button>
              <Button variant="solid" size="sm" onClick={handleUndoConfirm} disabled={undoLoading}>
                {undoLoading ? 'Undoing...' : 'Undo'}
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}

      {/* Conflict modal */}
      {showConflict && conflictData && (
        <Modal isOpen onClose={() => { setShowConflict(false); setConflictData(null); }}>
          <Modal.Header>Conflict Detected</Modal.Header>
          <Modal.Body>
            <Stack gap="sm">
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                This entity has been modified since your transaction. The current state differs from the expected state.
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Transaction:</strong> #{conflictData.transaction_id}
              </p>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" size="sm" onClick={() => { setShowConflict(false); setConflictData(null); }}>Cancel</Button>
              <Button variant="solid" size="sm" onClick={handleForceUndo} disabled={undoLoading}>
                {undoLoading ? 'Undoing...' : 'Undo Anyway'}
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
